import { ROLE_INFO, normalizeRole } from '@console/shared'
import { useQueryClient } from '@tanstack/react-query'
import { Building2, Mail } from 'lucide-react'
import { useState } from 'react'
import { Button } from '../components/ui/Button'
import { ErrorText } from '../components/ui/misc'
import { api, requestOwnOrganization, useMe } from '../lib/api'
import { authClient } from '../lib/auth'
import { fmtDate } from '../lib/format'

/**
 * Everyone works inside an organization. Someone here has an invitation waiting (they signed up through it): they
 * accept it, decline it, or start their own organization instead.
 */
export function NoOrganization() {
  const { data: me } = useMe()
  const [name, setName] = useState('')
  const [busy, setBusy] = useState<string | null>(null)
  const [err, setErr] = useState<unknown>(null)
  const qc = useQueryClient()
  const invites = me?.pendingInvitations ?? []

  const respond = async (id: string, accept: boolean) => {
    setBusy(id)
    setErr(null)
    const { error } = accept
      ? await authClient.organization.acceptInvitation({ invitationId: id })
      : await authClient.organization.rejectInvitation({ invitationId: id })
    setBusy(null)
    if (error) return setErr(error)
    await qc.invalidateQueries()
  }

  const [requested, setRequested] = useState(false)
  const request = async () => {
    setBusy('request')
    setErr(null)
    try {
      await requestOwnOrganization()
      setRequested(true)
    } catch (e) {
      setErr(e)
    } finally {
      setBusy(null)
    }
  }

  const create = async () => {
    setBusy('create')
    setErr(null)
    try {
      const { id } = await api<{ id: string }>('/api/orgs', { body: { name } })
      await authClient.organization.setActive({ organizationId: id })
      await qc.invalidateQueries()
    } catch (e) {
      setErr(e)
    } finally {
      setBusy(null)
    }
  }

  return (
    <div className="flex h-full items-center justify-center overflow-y-auto px-4">
      <div className="w-full max-w-md">
        <p className="eyebrow">Organizations</p>
        <h1 className="headline mt-3 text-[28px] leading-tight">
          <span className="pre">Everything here belongs to an organization.</span>
          {me?.canCreateOrg ? 'Join one, or start your own.' : 'Join one to get started.'}
        </h1>
        {invites.length > 0 && (
          <div className="mt-6 divide-y border-y border-line-subtle divide-[color:var(--border-subtle)]">
            {invites.map((i) => (
              <div key={i.id} className="flex items-center gap-3 px-1 py-3">
                <Mail className="h-4 w-4 shrink-0 text-ink-muted" strokeWidth={1.6} />
                <div className="min-w-0 flex-1">
                  <div className="truncate text-[13.5px] font-medium text-ink">{i.orgName}</div>
                  <div className="text-[12px] text-ink-muted">
                    as {ROLE_INFO[normalizeRole(i.role)].label} · expires {fmtDate(i.expiresAt)}
                  </div>
                </div>
                <Button variant="ghost" className="!h-8" disabled={!!busy} onClick={() => respond(i.id, false)}>
                  Decline
                </Button>
                <Button className="!h-8" loading={busy === i.id} disabled={!!busy && busy !== i.id} onClick={() => respond(i.id, true)}>
                  Join
                </Button>
              </div>
            ))}
          </div>
        )}
        {me?.canCreateOrg ? (
          <div className="mt-4 border-t border-line-subtle py-4">
            <div className="flex items-center gap-2 text-[13.5px] font-medium text-ink">
              <Building2 className="h-4 w-4 text-ink-muted" strokeWidth={1.6} /> New organization
            </div>
            <div className="mt-3 flex gap-2">
              <input className="input" placeholder="Organization name" value={name} onChange={(e) => setName(e.target.value)} />
              <Button onClick={create} loading={busy === 'create'} disabled={name.trim().length < 2 || !!busy}>
                Create
              </Button>
            </div>
          </div>
        ) : (
          <div className="mt-4 flex items-center justify-between gap-3 border-t border-line-subtle py-4">
            <span className="text-[13px] text-ink-secondary">{requested ? "Requested. We'll email you when it's ready." : 'Want an organization of your own?'}</span>
            {!requested && (
              <Button variant="secondary" loading={busy === 'request'} disabled={!!busy} onClick={request}>
                Request one
              </Button>
            )}
          </div>
        )}
        <div className="mt-3">
          <ErrorText error={err} />
        </div>
      </div>
    </div>
  )
}
